import { esc } from "../ui/format.js";
import { uiGet, uiMoney } from "../ui/kit.js";
import { emAct, emLetters, emLog, emRegister, emSlider, emSliderValue, emWireLetters, emWireSliders } from "./empire.js";

/**
 * Growth screens, 2026-10-22 round:
 *   Deals & Partners   contract tenders against your rival (price, term, service: the
 *                      odds move as you drag) and renewals that can churn (contracts.py)
 *   Marketing          the season calendar, a sale builder, viral moments (seasons.py)
 *   Operations, Workforce, Products   engineers against what products and research
 *                      need, and three ways to hire: directly (recruiting capacity and
 *                      employer brand), contractors, hubs abroad (staffing.py)
 *   R&D Lab            research speed from the engineers and the CTO seat
 */

// ---------------------------------------------------------------- contracts

function tenderCard(t, i) {
  return `<section class="mv-card ct-tender" data-ct="${t.id}">
    <h4>📑 ${esc(t.client)} is tendering a contract</h4>
    <p>${esc(t.sector)}, ${esc(t.country)} · about <b>${uiMoney(t.annual_value)}</b> a year at list price. <b>${esc(t.rival_name)}</b> is bidding too.
      ${t.due_month ? `Bids close at the end of month ${t.due_month}; if you don't bid, they win it.` : ""}</p>
    ${emSlider(`ct-price-${i}`, "Your price (% of list)", { min: 70, max: 110, step: 5, value: 95, unit: "pct" })}
    <div class="mv-chips" role="group" aria-label="Contract term">${[12, 24, 36].map((m) => `<button type="button" class="mv-chip${m === 24 ? " on" : ""}" data-ct-term="${m}">${m} months</button>`).join("")}</div>
    <div class="mv-chips" role="group" aria-label="Service level">
      <button type="button" class="mv-chip on" data-ct-sla="standard">Standard service</button>
      <button type="button" class="mv-chip" data-ct-sla="premium" title="Costs ${t.premium_setup_pct}% of the first year to set up; clients like it">Premium service (+${t.premium_setup_pct}% setup)</button></div>
    <p class="ct-odds" aria-live="polite">Win odds <b data-ct-odds>—</b> · you'd earn <b data-ct-value>—</b> a year</p>
    <div class="em-actions"><button type="button" class="primary" data-ct-bid>Submit the bid</button></div>
  </section>`;
}

async function contractsPanel(host) {
  const v = await uiGet("/contracts");
  if (!v.tenders.length && !v.renewals.length && !v.history.length && !v.letters.length) return null;
  host.innerHTML = `
    ${emLetters(v.letters)}
    <p class="sx-card-sub">${esc(v.how)}</p>
    <div class="mv-grid">${v.tenders.map(tenderCard).join("") || `<section class="mv-card"><h4>📑 No tender open</h4><p class="sx-card-sub">Big clients put contracts out to bid now and then; your rival always bids.</p></section>`}
      <section class="mv-card"><h4>🔁 Your contracts and their renewals</h4>
        ${v.renewals.length ? v.renewals.map((r) => `<div class="mv-row"><span><b>${esc(r.client)}</b> · ${uiMoney(r.monthly_revenue, { compact: true })}/mo · renews ${r.months_left <= 0 ? "now" : `in ${r.months_left} mo`}</span>
          <span class="${r.odds_pct >= 75 ? "pos" : r.odds_pct < 50 ? "neg" : ""}" title="Satisfaction ${r.satisfaction}/100, ${r.breaches} SLA breach(es)">${r.odds_pct}% renew</span></div>`).join("")
          : `<p class="sx-card-sub">Win a tender or sign a contract on this tab and it shows here.</p>`}
        <p class="sx-card-sub">Renewal odds follow the product's satisfaction, any SLA breaches and your rival's counter-offer.</p></section>
      <section class="mv-card"><h4>🏁 Bidding record: ${v.won} won, ${v.lost} lost</h4>${emLog(v.history.map((h) => ({ month: h.closed_month, text: h.result })), { emptyText: "No bids yet." })}</section>
    </div>`;
  emWireLetters(host);
  v.tenders.forEach((t, i) => {
    const card = host.querySelector(`[data-ct="${t.id}"]`);
    let term = 24, sla = "standard";
    const upd = () => {
      const price = emSliderValue(card, `ct-price-${i}`);
      const odds = t.grid[`${price}|${term}|${sla}`];
      card.querySelector("[data-ct-odds]").textContent = odds != null ? `${Math.round(odds * 100)}%` : "—";
      card.querySelector("[data-ct-value]").textContent = uiMoney(t.annual_value * price / 100);
    };
    const pick = (sel, attr, set) => card.querySelectorAll(sel).forEach((b) => b.addEventListener("click", () => {
      card.querySelectorAll(sel).forEach((x) => x.classList.toggle("on", x === b)); set(b.dataset[attr]); upd();
    }));
    pick("[data-ct-term]", "ctTerm", (x) => { term = +x; });
    pick("[data-ct-sla]", "ctSla", (x) => { sla = x; });
    emWireSliders(card, upd);
    card.querySelector("[data-ct-bid]").addEventListener("click", () =>
      emAct("contract_bid", { tender_id: t.id, price_pct: emSliderValue(card, `ct-price-${i}`), term, sla }));
  });
  const due = v.tenders.length + v.letters.length;
  return { title: "Contracts: bidding wars and renewals", summary: `${v.tenders.length} tender(s) open · ${v.renewals.length} contract(s)`, open: due > 0, badge: due ? "!" : "" };
}

/** Deals & Partners › Tenders (2.7 sub-tabs): open tenders, the bidding record. */
function dealsTendersPane(host, v) {
  host.innerHTML = `${emLetters(v.letters.filter((d) => d.kind !== "renewal"))}
    <p class="sx-card-sub">${esc(v.how)}</p>
    <div class="mv-grid">${v.tenders.map(tenderCard).join("") || `<section class="mv-card"><h4>📑 No tender open</h4><p class="sx-card-sub">Big clients put contracts out to bid now and then; your rival always bids.</p></section>`}
      <section class="mv-card"><h4>🏁 Bidding record: ${v.won} won, ${v.lost} lost</h4>${emLog(v.history.map((h) => ({ month: h.closed_month, text: h.result })), { emptyText: "No bids yet." })}</section>
    </div>`;
  emWireLetters(host);
  v.tenders.forEach((t, i) => {
    const card = host.querySelector(`[data-ct="${t.id}"]`);
    let term = 24, sla = "standard";
    const upd = () => {
      const price = emSliderValue(card, `ct-price-${i}`);
      const odds = t.grid[`${price}|${term}|${sla}`];
      card.querySelector("[data-ct-odds]").textContent = odds != null ? `${Math.round(odds * 100)}%` : "—";
      card.querySelector("[data-ct-value]").textContent = uiMoney(t.annual_value * price / 100);
    };
    const pick = (sel, attr, set) => card.querySelectorAll(sel).forEach((b) => b.addEventListener("click", () => {
      card.querySelectorAll(sel).forEach((x) => x.classList.toggle("on", x === b)); set(b.dataset[attr]); upd();
    }));
    pick("[data-ct-term]", "ctTerm", (x) => { term = +x; });
    pick("[data-ct-sla]", "ctSla", (x) => { sla = x; });
    emWireSliders(card, upd);
    card.querySelector("[data-ct-bid]").addEventListener("click", () =>
      emAct("contract_bid", { tender_id: t.id, price_pct: emSliderValue(card, `ct-price-${i}`), term, sla }));
  });
}

/** Deals & Partners › Contracts: renewal letters first, then every contract with its renewal odds. */
function dealsContractsPane(host, v) {
  host.innerHTML = `${emLetters(v.letters.filter((d) => d.kind === "renewal"))}
    <section class="mv-card"><h4>🔁 Your contracts and their renewals</h4>
      ${v.renewals.length ? v.renewals.map((r) => `<div class="mv-row"><span><b>${esc(r.client)}</b> · ${uiMoney(r.monthly_revenue, { compact: true })}/mo · renews ${r.months_left <= 0 ? "now" : `in ${r.months_left} mo`}</span>
        <span class="${r.odds_pct >= 75 ? "pos" : r.odds_pct < 50 ? "neg" : ""}" title="Satisfaction ${r.satisfaction}/100, ${r.breaches} SLA breach(es)">${r.odds_pct}% renew</span></div>`).join("")
        : `<p class="sx-card-sub">Win a tender or sign a contract and it shows here.</p>`}
      <p class="sx-card-sub">Renewal odds follow the product's satisfaction, any SLA breaches and your rival's counter-offer.</p></section>`;
  emWireLetters(host);
}

window.dealsTendersPane = dealsTendersPane;
window.dealsContractsPane = dealsContractsPane;

// ---------------------------------------------------------------- seasons

async function seasonsPanel(host) {
  const v = await uiGet("/seasons");
  const now = v.now[0];
  const soon = v.calendar.slice(0, 6);
  host.innerHTML = `
    ${emLetters(v.letters)}
    <p class="sx-card-sub">It's <b>${esc(v.calendar_month)}</b> in the game. ${esc(v.how)}</p>
    <div class="ss-cal">${soon.map((s) => `<div class="ss-season${s.now ? " now" : ""}" title="${esc(s.what)} · ${esc(s.where)}">
      <span class="ss-i">${s.icon}</span><b>${esc(s.name)}</b><span>${s.now ? "Now" : `in ${s.months_away} mo`} · ${esc(s.month)}</span>
      <span class="ss-fit">fit ${Math.round(s.fit * 100)}%</span></div>`).join("")}</div>
    <section class="mv-card"><h4>🏷 Run a sale ${v.ran_this_month ? "<span class='sx-card-sub'>(one a month: done)</span>" : ""}</h4>
      <div class="mv-chips" role="group" aria-label="Which sale">
        ${v.now.map((s, i) => `<button type="button" class="mv-chip${i === 0 ? " on" : ""}" data-ss="${esc(s.key)}">${s.icon} ${esc(s.name)}</button>`).join("")}
        <button type="button" class="mv-chip${now ? "" : " on"}" data-ss="flash">⚡ Flash sale (any month, half strength)</button></div>
      ${emSlider("ss-disc", "Discount", { min: 0, max: 40, step: 5, value: 15, unit: "pct" })}
      ${emSlider("ss-budget", "Ad budget", { min: 0, max: v.budget_max, step: 5000, value: Math.min(v.budget_max, v.budget_suggest || 20000) })}
      <p class="ct-odds" aria-live="polite" data-ss-quote>Working it out…</p>
      <div class="em-actions"><button type="button" class="primary" data-ss-go ${v.ran_this_month ? "disabled" : ""}>Launch the sale</button></div></section>
    <section class="mv-card"><h4>Recent sales and moments</h4>${emLog(v.sales.map((s) => ({ month: s.month, text: s.result })).concat(v.recent), { emptyText: "No sales yet." })}</section>`;
  emWireLetters(host);
  let season = now ? now.key : "flash";
  let timer, firstQuote = true;
  const quote = () => {
    clearTimeout(timer);
    const wait = firstQuote ? 0 : 180; // the first quote at once, the wait only while dragging
    firstQuote = false;
    timer = setTimeout(async () => {
      try {
        const q = await uiGet(`/seasons/quote?season=${encodeURIComponent(season)}&discount_pct=${emSliderValue(host, "ss-disc")}&budget=${emSliderValue(host, "ss-budget")}`);
        const el = host.querySelector("[data-ss-quote]");
        if (el) el.innerHTML = `${esc(q.name)}: about <b>${uiMoney(q.est_extra_sales)}</b> extra sales; costs ${uiMoney(q.discount_cost + q.budget)}.
          <b class="${q.est_profit >= 0 ? "pos" : "neg"}">${esc(q.verdict)}</b>${q.brand_hit ? " · a discount this deep cheapens the brand" : ""}`;
      } catch (e) { /* the panel stays usable without a quote */ }
    }, wait);
  };
  host.querySelectorAll("[data-ss]").forEach((b) => b.addEventListener("click", () => {
    host.querySelectorAll("[data-ss]").forEach((x) => x.classList.toggle("on", x === b)); season = b.dataset.ss; quote();
  }));
  emWireSliders(host, quote);
  host.querySelector("[data-ss-go]").addEventListener("click", () =>
    emAct("season_campaign", { season, discount_pct: emSliderValue(host, "ss-disc"), budget: emSliderValue(host, "ss-budget") }));
  return { title: "Seasons, sales and viral moments", summary: now ? `${now.icon} ${now.name} is on now` : `next: ${soon[0] ? soon[0].name : ""}`,
           open: !!now || v.letters.length > 0, badge: v.letters.length ? "!" : "" };
}

// ---------------------------------------------------------------- engineers

const engN = (n) => Math.round(n || 0).toLocaleString("en-US");
const ENG_LEVEL_LABEL = { junior: "Junior", senior: "Senior", star: "⭐ Star" };

function engBrandHtml(b) {
  return `<div class="eng-brand"><div class="eng-brand-head"><span>Employer brand</span><b>${Math.round(b.score)}/100</b><em>${esc(b.label)}</em></div>
    <div class="eng-brand-bar"><i style="width:${Math.max(3, b.score)}%"></i></div>
    <div class="eng-brand-parts">${b.parts.map((p) => `<span class="${p.points >= 0 ? "pos" : "neg"}">${esc(p.label)} ${p.points >= 0 ? "+" : ""}${p.points}</span>`).join("")}</div></div>`;
}

/** Need vs have for every engineering specialization the industry uses (staffing.reading specs). */
function engSpecsHtml(v) {
  const rows = (v.specs || []).filter((s) => s.need > 0 || s.have > 0);
  if (!rows.length) return "";
  const top = Math.max(1, ...rows.map((s) => Math.max(s.need, s.have)));
  return `<div class="eng-specs" role="table" aria-label="Engineers by specialization">
    <div class="eng-spec head" role="row"><span>Specialization</span><span>Have vs need</span><span>Gap</span><span>Pay</span></div>
    ${rows.map((s) => `<div class="eng-spec ${s.gap > 0 ? "short" : ""}" role="row" title="${esc(s.what)}">
      <span><b>${esc(s.spec)}</b><em>${esc(s.what)} · ${s.share_pct}% of the team</em></span>
      <span class="eng-spec-bars"><i class="have" style="width:${((100 * s.have) / top).toFixed(1)}%"></i><i class="need" style="left:${Math.min(99.5, (100 * s.need) / top).toFixed(1)}%"></i>
        <em>${engN(s.have)} / ${engN(s.need)}</em></span>
      <span class="${s.gap > 0 ? "neg" : "pos"}">${s.gap > 0 ? `−${engN(s.gap)}` : "✓"}</span>
      <span>${s.pay_x.toFixed(2)}×</span></div>`).join("")}</div>`;
}

function engSpecSelect(id, v) {
  const gaps = (v.specs || []).filter((s) => s.need > 0).sort((a, b) => b.gap - a.gap);
  const opts = gaps.length ? gaps : Object.keys(v.spec_info || {}).map((k) => ({ spec: k, gap: 0 }));
  return `<label class="iv-slide">Specialization <select id="${id}">${opts.map((s) =>
    `<option value="${esc(s.spec)}">${esc(s.spec)}${s.gap ? ` · short ${engN(s.gap)}` : ""}</option>`).join("")}</select></label>`;
}

function engStarsHtml(v) {
  const s = v.stars || {};
  if (!s.count) return `<p class="sx-card-sub">⭐ <b>Stars</b> cost 3× a senior and ship 2.5× as much. Rivals try to hire them away (${s.risk_pct || 3}% a month each) unless they hold vesting stock. You have none yet.</p>`;
  return `<section class="eng-ch eng-stars"><h4>⭐ Your star engineers: ${engN(s.count)}</h4>
    <p class="sx-card-sub">${engN(s.vested)} hold vesting stock (rivals tempt ${s.risk_vested_pct}% a month away); ${engN(s.unvested)} don't (${s.risk_pct}% a month each). A star who leaves sets research back.</p>
    ${s.unvested ? `<p class="sx-card-sub">A 4-year grant for the ${engN(s.unvested)} without stock is worth ${uiMoney(s.grant_value, { compact: true })}, paid in new shares: every owner, you included, is diluted ${s.dilution_pct.toFixed(3)}%.</p>
      <div class="em-actions"><button type="button" class="primary" data-eng-equity>Give ${engN(s.unvested)} stars vesting stock</button></div>` : ""}</section>`;
}

function engHtml(v, { hire = true } = {}) {
  const cov = v.product_coverage_pct;
  const p = v.plan || {};
  const facts = `<p class="eng-verdict eng-${esc(v.tone)}">${esc(v.verdict)}</p>
    <div class="eng-facts">
      <span title="Your own engineers (R&D budget plus the dedicated team)">Own engineers <b>${engN(v.engineers)}</b>${v.dedicated ? ` (${engN(v.dedicated)} dedicated)` : ""}</span>
      ${v.contractors ? `<span title="Count at 80%">Contractors <b>${engN(v.contractors)}</b></span>` : ""}
      ${v.hub_engineers ? `<span title="Count at 90%">In hubs <b>${engN(v.hub_engineers)}</b></span>` : ""}
      <span title="What your products' revenue needs">Products need <b>${engN(v.needed_products)}</b></span>
      <span title="What the running research project needs">Research needs <b>${engN(v.needed_research)}</b></span>
      <span title="Short-staffed products ship less of their demand">Output <b class="${cov < 100 ? "neg" : "pos"}">${v.output_pct}%</b></span>
      <span title="Research speed from the engineers left after products, x0.7 with no CTO">Research speed <b>${v.research_speed_pct}%</b></span>
      <span>CTO <b class="${v.has_cto ? "pos" : "neg"}">${v.has_cto ? "in the seat" : "seat empty: slower research and updates"}</b></span>
    </div>
    ${engSpecsHtml(v)}`;
  if (!hire) return `${facts}<p class="sx-card-sub">${esc(v.how)}</p>`;
  const conMax = Math.max(1, v.contractor_cap - v.contractors);
  const dirMax = Math.max(1, v.capacity_left);
  const opts = v.hub_options || [];
  const lv = v.levels || {};
  const auto = `<div class="eng-auto${v.auto ? " on" : ""}"><b>🤖 Auto-staffing ${v.auto ? "on" : "off"}</b>
      <span>${v.auto ? "Every month your recruiters hire to cover what products and research need, bring in contractors for the rest, open or grow a hub abroad for big gaps, and end spare contracts. They only hire what cash can pay for 3 months." : "You hire engineers yourself. Switch it on and the company staffs itself to need every month."}</span>
      <button type="button" data-eng-auto="${v.auto ? "0" : "1"}">${v.auto ? "Switch off" : "Switch on"}</button></div>
    ${(v.log || []).length ? `<details class="eng-log"><summary>Recent hiring</summary>${emLog(v.log, { limit: 6 })}</details>` : ""}`;
  return `${facts}
    ${auto}
    ${v.short_by > 0 ? `<div class="eng-plan"><b>Fastest way to close the gap of ${engN(v.short_by)}:</b>
      ${p.direct ? `hire ${engN(p.direct)} directly` : ""}${p.direct && p.contractors ? ", " : ""}${p.contractors ? `bring in ${engN(p.contractors)} contractors` : ""}${p.still_short ? `; the last ${engN(p.still_short)} take about ${p.months_to_close} more month${p.months_to_close === 1 ? "" : "s"} of hiring (or a hub abroad)` : ""}.
      Raising the R&D budget in Operations also grows the base team in the right mix (it funds ${engN(v.budget_funded)} today).</div>` : ""}
    ${engBrandHtml(v.employer_brand)}
    <div class="eng-channels">
      <section class="eng-ch"><h4>🧑‍💼 Hire directly</h4>
        <p class="sx-card-sub">Your recruiters can bring in <b>${engN(v.capacity)}</b> a month (${engN(v.capacity_left)} left this month; up to ${engN(v.star_capacity)} stars). They join next month and stay through budget cuts.</p>
        ${engSpecSelect("eng-spec", v)}
        <div class="mv-chips" role="radiogroup" aria-label="Level">${["junior", "senior", "star"].map((k) => `<button type="button" class="mv-chip${k === "senior" ? " on" : ""}" data-eng-level="${k}"
          title="${esc((lv[k] || {}).what || "")}">${ENG_LEVEL_LABEL[k]} · ${(lv[k] || {}).pay_x || 1}× pay · ${(lv[k] || {}).output_x || 1}× output</button>`).join("")}</div>
        ${emSlider("eng-n", "Engineers", { min: 1, max: dirMax, step: 1, value: Math.max(1, Math.min(dirMax, p.direct || v.short_by || 5)), unit: "count", hint: " " })}
        <div class="em-actions"><button type="button" class="primary" data-eng-hire ${v.capacity_left ? "" : "disabled"}>Hire engineers</button></div></section>
      <section class="eng-ch"><h4>🤝 Contractors</h4>
        <p class="sx-card-sub">An IT-services firm supplies them this month: no fee, 1.6× pay, 80% as productive. Up to ${engN(v.contractor_cap)}.</p>
        ${engSpecSelect("eng-c-spec", v)}
        ${emSlider("eng-c", "Contract engineers", { min: 1, max: conMax, step: 1, value: Math.max(1, Math.min(conMax, p.contractors || 10)), unit: "count", hint: " " })}
        <div class="em-actions"><button type="button" class="primary" data-eng-con ${v.contractor_cap > v.contractors ? "" : "disabled"}>Bring them in</button></div>
        ${v.contractors ? `${emSlider("eng-c-end", "Contracts to end", { min: 1, max: v.contractors, step: 1, value: v.contractors, unit: "count", hint: " " })}
          <div class="em-actions"><button type="button" data-eng-con-end>End contracts</button></div>` : ""}</section>
      <section class="eng-ch"><h4>🌍 Engineering hub abroad</h4>
        <p class="sx-card-sub">Pay follows each country's income (World Bank). It fills over months; setup is cheaper where you already operate.</p>
        ${opts.length ? `<label class="iv-slide">Country <select data-eng-hub-country>${opts.map((o) => `<option value="${esc(o.country)}">${esc(o.country)} · pay ${o.pay_vs_home_pct}% of home · ${o.output_pct || 90}% as productive · ${esc(o.climate)}${o.present ? " · you're there" : ""}</option>`).join("")}</select></label>
        ${engSpecSelect("eng-h-spec", v)}
        ${emSlider("eng-h", "Seats", { min: 10, max: Math.max(20, opts[0].max_target), step: 10, value: Math.min(opts[0].max_target, Math.max(20, Math.round((p.still_short || 100) / 10) * 10)), unit: "count", hint: " " })}
        <div class="em-actions"><button type="button" class="primary" data-eng-hub>Open the hub</button></div>` : `<p class="sx-card-sub">No more countries to open in.</p>`}</section>
    </div>
    ${engStarsHtml(v)}
    ${(v.team || []).length ? `<div class="eng-team"><b>Your dedicated team and contractors</b>${v.team.map((t) => `<span>${engN(t.count)} ${t.level === "contractor" ? "contract" : esc(t.level)} ${esc(t.spec)} · ${uiMoney(t.monthly, { compact: true })}/mo</span>`).join("")}</div>` : ""}
    ${(v.hubs || []).length ? `<div class="eng-hubs">${v.hubs.map((h) => `<div class="eng-hub"><b>${esc(h.country)}</b>
      <div class="eng-hub-bar"><i style="width:${Math.min(100, (100 * h.staffed) / Math.max(1, h.target)).toFixed(0)}%"></i></div>
      <span>${engN(h.staffed)} / ${engN(h.target)} ${esc(h.spec || "")} · ${uiMoney(h.monthly_each)} each a month</span>
      <button type="button" data-eng-hub-close="${esc(h.country)}">Close</button></div>`).join("")}</div>` : ""}
    ${v.dedicated ? `<div class="em-actions"><button type="button" data-eng-release>Let ${engN(Math.min(v.dedicated, Math.max(5, Math.round(v.dedicated * 0.1))))} dedicated engineers go (juniors first)</button></div>` : ""}
    <p class="sx-card-sub">${esc(v.how)}</p>`;
}

async function engineeringPanel(host, opts) {
  const v = await uiGet("/engineering");
  host.innerHTML = engHtml(v, opts);
  const hubOpts = v.hub_options || [];
  const hubSel = host.querySelector("[data-eng-hub-country]");
  const hubPick = () => hubOpts.find((o) => o.country === (hubSel ? hubSel.value : "")) || hubOpts[0];
  let level = "senior";
  const specPay = (id) => { const el = host.querySelector(`#${id}`); return el && v.spec_info[el.value] ? v.spec_info[el.value].pay_x : 1; };
  const levelPay = () => ((v.levels || {})[level] || {}).pay_x || 1;
  const hint = (id, n) => {
    const h = host.querySelector(`#${id}-hint`);
    if (!h) return;
    if (id === "eng-n") {
      const x = specPay("eng-spec") * levelPay();
      h.textContent = `${uiMoney(n * v.hire_fee_each * x)} in fees now, then ${uiMoney(n * v.monthly_cost_each * x)} a month.`;
    }
    if (id === "eng-c") h.textContent = `${uiMoney(n * v.contractor_monthly_each * specPay("eng-c-spec"))} a month, from this month. Counts as ${engN(n * 0.8)} engineers.`;
    if (id === "eng-h") {
      const o = hubPick();
      if (!o) return;
      const setup = (v.hub_setup.base + v.hub_setup.each * n) * (o.present ? 1 - v.hub_setup.present_discount : 1);
      h.textContent = `Setup about ${uiMoney(setup, { compact: true })}; then ${uiMoney(n * v.monthly_cost_each * specPay("eng-h-spec") * o.pay_vs_home_pct / 100, { compact: true })} a month when full. Talent pool there: ${engN(o.pool)} engineers (room for ${engN(o.max_target)}).`;
    }
  };
  emWireSliders(host, hint);
  const refresh = (id) => { const r = host.querySelector(`#${id}`); if (r) hint(id, +r.value); };
  host.querySelectorAll("[data-eng-level]").forEach((b) => b.addEventListener("click", () => {
    level = b.dataset.engLevel;
    host.querySelectorAll("[data-eng-level]").forEach((x) => x.classList.toggle("on", x === b));
    const r = host.querySelector("#eng-n");
    if (r && level === "star") { r.max = Math.max(1, Math.min(+r.max, v.star_capacity)); if (+r.value > +r.max) r.value = r.max; }
    if (r && level !== "star") r.max = Math.max(1, v.capacity_left);
    if (r) r.dispatchEvent(new Event("input"));
  }));
  ["eng-spec", "eng-c-spec", "eng-h-spec"].forEach((id) => {
    const el = host.querySelector(`#${id}`);
    if (el) el.addEventListener("change", () => refresh({ "eng-spec": "eng-n", "eng-c-spec": "eng-c", "eng-h-spec": "eng-h" }[id]));
  });
  if (hubSel) hubSel.addEventListener("change", () => {
    const r = host.querySelector("#eng-h");
    r.max = Math.max(20, hubPick().max_target);
    if (+r.value > +r.max) r.value = r.max;
    r.dispatchEvent(new Event("input"));
  });
  const val = (id) => { const el = host.querySelector(`#${id}`); return el ? el.value : undefined; };
  const on = (sel, fn) => { const b = host.querySelector(sel); if (b) b.addEventListener("click", fn); };
  on("[data-eng-hire]", () => emAct("hire_engineers", { count: emSliderValue(host, "eng-n"), spec: val("eng-spec"), level }));
  on("[data-eng-con]", () => emAct("hire_engineers", { channel: "contractors", count: emSliderValue(host, "eng-c"), spec: val("eng-c-spec") }));
  on("[data-eng-con-end]", () => emAct("release_engineers", { channel: "contractors", count: emSliderValue(host, "eng-c-end") }));
  on("[data-eng-auto]", (e) => emAct("auto_staffing", { on: e.currentTarget.dataset.engAuto === "1" }));
  on("[data-eng-hub]", () => emAct("hire_engineers", { channel: "hub", country: hubPick().country, count: emSliderValue(host, "eng-h"), spec: val("eng-h-spec") }));
  on("[data-eng-release]", () => emAct("release_engineers", { count: Math.min(v.dedicated, Math.max(5, Math.round(v.dedicated * 0.1))) }));
  on("[data-eng-equity]", () => emAct("stars_equity", {}));
  host.querySelectorAll("[data-eng-hub-close]").forEach((b) => b.addEventListener("click", () => {
    if (confirm(`Close the ${b.dataset.engHubClose} hub? Its engineers get 3 months' severance; morale and trust there fall.`)) emAct("release_engineers", { channel: "hub", country: b.dataset.engHubClose });
  }));
  return { title: "Engineers: what your products and research need", summary: `${engN(v.effective)} engineers · output ${v.output_pct}% · research ${v.research_speed_pct}%`,
           open: v.tone === "bad", badge: v.tone === "bad" ? "!" : "" };
}

emRegister("ambassadors", seasonsPanel);
emRegister("marketing", seasonsPanel);
emRegister("operations", (h) => engineeringPanel(h));
emRegister("workforce", (h) => engineeringPanel(h));
emRegister("rnd", (h) => engineeringPanel(h, { hire: false }));
emRegister("products", (h) => engineeringPanel(h, { hire: false }), { bottom: true });

export { contractsPanel, engineeringPanel, seasonsPanel };
